import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AuthContainer from '../../../components/auth/AdminAuthContainer';
import AuthHeader from '../../../components/auth/AdminAuthHeader';
import Card from '../../../components/ui/Card';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

import { toast } from 'react-toastify';
import authService from '../../../services/admin/authService';
import {
  validatePassword,
  validateConfirmPassword,
  validateVerificationCode,
  generatePasswordRequirements,
} from '../../../utils/validation/passwordValidation';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Get userId and email from location state or local storage
  const [userId, setUserId] = useState(location.state?.userId || localStorage.getItem('reset_userId') || '');
  const [userEmail, setUserEmail] = useState(location.state?.email || localStorage.getItem('reset_email') || '');
  
  const [formData, setFormData] = useState({
    code: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Verify code, 2: Reset password
  
  useEffect(() => {
    // If no userId, redirect to forgot password
    if (!userId) {
      toast.error('Reset session expired. Please start over.');
      navigate('/hpanel/forgot-password');
    }
  }, [userId, navigate]);
  

   const handleChange = (e) => {
    const { name, value } = e.target;
    //console.log('Input Change:', name, value);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Calculate password strength
    if (name === 'newPassword') {
      const validation = validatePassword(value);
      setPasswordStrength(validation.strength);
    }
    //console.log('Current Form Data:', { ...formData, [name]: value });
  };

  // Handle password reset submission
  const handleSubmit = async (e) => {
    //console.log('Form Submitted with data:', formData)
    e.preventDefault();
    
    // Validate password
    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setErrors(prev => ({
        ...prev,
        newPassword: passwordValidation.errors[0]
      }));
      return;
    }
    
    // Validate confirm password
    const confirmValidation = validateConfirmPassword(formData.newPassword, formData.confirmPassword);
    if (!confirmValidation.isValid) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: confirmValidation.error
      }));
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authService.resetPassword(
        userId,
        formData.code,
        formData.newPassword,
        formData.confirmPassword
      );
      
      toast.success(response.message || 'Password reset successfully!');
      
      // Clear reset data from session
      localStorage.removeItem('reset_userId');
      localStorage.removeItem('reset_email');
      
      // Redirect to login page
      setTimeout(() => {
        navigate('/hpanel/login', {
          state: { 
            message: 'Password reset successful. Please login with your new password.',
            email: userEmail
          }
        });
      }, 1500);
      
    } catch (error) {
      // Handle specific errors
      if (error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach(err => {
          if (err.path) {
            serverErrors[err.path] = err.msg;
          }
        });
        setErrors(serverErrors);
      } else if (error.response?.data?.message) {
        //toast.error(error.response.data.message);
      } else {
        toast.error('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    setLoading(true);
    
    try {
      const response = await authService.resendVerificationCode(userId);
      toast.success(response.message || 'Verification code resent');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        {/* SEO Settings */}
        <title>{`Rest Password | ${process.env.REACT_APP_NAME}`}</title>
        <meta name="robots" content="noindex, nofollow" />

        <AuthContainer>
          <AuthHeader 
            title="Admin Password Reset Successfully" 
            subtitle="You can now log in to the admin panel with your new password" 
          />
          
          <Card>
            <div className="text-center py-8">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-check text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Admin Password Updated!</h3>
              <p className="text-gray-600 mb-6">
                Your admin password has been reset successfully. Redirecting to admin login page...
              </p>
              <Link to="/hpanel/login">
                <Button variant="primary">
                  Go to Admin Login
                </Button>
              </Link>
            </div>
          </Card>
        </AuthContainer>
      </>
    );
  }

  return (
    <>
      {/* SEO Settings */}
      <title>{`Rest Password | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />
      
      <AuthContainer>
        <AuthHeader />
        
        <Card>
          <form onSubmit={handleSubmit}>
            <Input
              type="text"
              name="code"
              label="Verification Code"
              icon="fas fa-pencil"
              placeholder="Verification Code"
              value={formData.code}
              onChange={handleChange}
              required
            />
            
            <Input
              type="password"
              name="newPassword"
              label="New Admin Password"
              icon="fas fa-lock"
              placeholder="••••••••"
              value={formData.newPassword}
              onChange={handleChange}
              required
            />
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
            )}
            {/* <div className="mt-2 text-xs text-gray-500 mb-4">
              <p>Password must contain at least 6 characters.</p>
            </div> */}
            
            <Input
              type="password"
              name="confirmPassword"
              label="Confirm New Admin Password"
              icon="fas fa-lock"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
            )}

            <Link 
              className="text-primary-600 text-sm font-medium hover:text-primary-700"
              onClick={handleResendCode}
            >
              Resend Verification Code
            </Link>
            
            <Button type="submit" variant="primary" className="w-full mb-4 mt-4" disabled={loading}>
              { loading ? ('Please wait...') : ('Reset Password') }
            </Button>
            
            <div className="text-center">
              <Link 
                to="/hpanel/login" 
                className="text-primary-600 font-medium hover:text-primary-700 flex items-center justify-center"
              >
                <i className="fas fa-arrow-left mr-2"></i> Back to Admin Sign In
              </Link>
            </div>
          </form>
        </Card>
      </AuthContainer>
    </>
  );
};

export default ResetPassword;